import React, { useState, useEffect } from 'react';
import { ArrowLeft, Sparkles, Clock, Star } from 'lucide-react';
import { updateUserProgress } from '../services/dbService';
import DailyMissionBoard from './DailyMissionBoard';

const localDateString = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const ECONOMY_POWER_UPS = {
  DOUBLE_POINTS: { cost: 50, duration: 600000, multiplier: 2, name: 'Double Points' },
  STREAK_FREEZE: { cost: 75, duration: 86400000, name: 'Streak Freeze' },
  EXTRA_TIME: { cost: 25, duration: 600000, extraTime: 60, name: 'Extra Time' },
  POINT_SHIELD: { cost: 100, duration: 1800000, name: 'Point Shield' },
  QUIZ_ATTEMPTS_5: { cost: 100, quizAttempts: 5, name: '+5 Quiz Attempts' },
  QUIZ_ATTEMPTS_10: { cost: 180, quizAttempts: 10, name: '+10 Quiz Attempts' }
};

const DailyChestsPage = ({ onCancel, userData, setUserData, userId, showToast, onNavigate, onStartQuiz }) => {
  const [dailyChestsData, setDailyChestsData] = useState({});
  const [opening, setOpening] = useState(null);
  const [showRewards, setShowRewards] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  const getMidnightExpiry = () => {
    const midnight = new Date();
    midnight.setHours(24, 0, 0, 0);
    return midnight.getTime();
  };

  // Check if a chest is available based on time restrictions
  const isChestAvailable = (chestId) => {
    const hour = currentTime.getHours();

    if (chestId === 'daily-chest-1') {
      // Morning Blessing: 3am to 2:59pm (hours 3-14)
      return hour >= 3 && hour < 15;
    } else if (chestId === 'daily-chest-2') {
      // Evening Grace: 3pm to 2:59am (hours 15-23, 0-2)
      return hour >= 15 || hour < 3;
    }

    return true;
  };

  // Get time restriction message for a chest
  const getTimeRestrictionMessage = (chestId) => {
    if (chestId === 'daily-chest-1') {
      return 'Available 3:00 AM - 2:59 PM';
    } else if (chestId === 'daily-chest-2') {
      return 'Available 3:00 PM - 2:59 AM';
    }
    return '';
  };

  // Daily chests - 2 free per day
  const DAILY_CHESTS = [
    {
      id: 'daily-chest-1',
      title: 'Morning Blessing',
      subtitle: 'Earn Manna with each quiz today!',
      icon: `${process.env.PUBLIC_URL || ''}/manna 4.png`,
      isImage: true,
      color: 'from-amber-400 to-orange-500',
      glowColor: 'shadow-amber-500/50',
      rewards: [
        { type: 'manna-activation', description: 'Earn 1 Manna per quiz completed today' }
      ]
    },
    {
      id: 'daily-chest-2',
      title: 'Evening Grace',
      subtitle: '5 free Keys + 1 hour protection',
      icon: '🔑',
      color: 'from-cyan-500 to-blue-600',
      glowColor: 'shadow-cyan-500/50',
      rewards: [
        { type: 'keys', amount: 5 },
        { type: 'protection', duration: 3600000, description: '1 hour protection from quiz penalties' }
      ]
    }
  ];

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Load daily chests data from localStorage
  useEffect(() => {
    const today = localDateString();
    const saved = localStorage.getItem('freeDailyChests');

    if (saved) {
      try {
        const data = JSON.parse(saved);
        // If data is from today, use it; otherwise reset
        if (data.date === today) {
          setDailyChestsData(data);
        } else {
          // New day - reset
          const newData = {
            date: today,
            opened: []
          };
          setDailyChestsData(newData);
          localStorage.setItem('freeDailyChests', JSON.stringify(newData));
        }
      } catch (e) {
        console.error('Error loading daily chests:', e);
      }
    } else {
      // Initialize
      const newData = {
        date: today,
        opened: []
      };
      setDailyChestsData(newData);
      localStorage.setItem('freeDailyChests', JSON.stringify(newData));
    }
  }, []);

  // Check if chest is opened today
  const isChestOpened = (chestId) => {
    return dailyChestsData.opened?.includes(chestId) || false;
  };

  // Open chest with animation
  const openChest = (chest) => {
    if (isChestOpened(chest.id) || opening) return;

    // Check time restrictions
    if (!isChestAvailable(chest.id)) {
      showToast(`${chest.title} is not available at this time.\n${getTimeRestrictionMessage(chest.id)}`, 'error');
      return;
    }

    setOpening(chest.id);

    // Show opening animation for 1.5 seconds
    setTimeout(() => {
      let newPoints = userData.totalPoints || 0;
      let newManna = userData.manna || 0;
      let newKeys = userData.keys || 0;
      const newActiveBoosts = [...(userData.activeBoosts || [])];

      let mannaEarningActivated = false;

      // Process rewards
      chest.rewards.forEach(reward => {
        if (reward.type === 'points') {
          newPoints += reward.amount;
        } else if (reward.type === 'manna') {
          newManna += reward.amount;
        } else if (reward.type === 'manna-activation') {
          mannaEarningActivated = true;
        } else if (reward.type === 'keys') {
          newKeys += reward.amount;
        } else if (reward.type === 'protection') {
          newActiveBoosts.push({
            type: 'PENALTY_PROTECTION',
            name: 'Quiz Penalty Protection',
            activatedAt: Date.now(),
            expiresAt: Date.now() + (reward.duration || 3600000),
            penaltyProtection: true
          });
        } else if (reward.type === 'keys-boost') {
          const expiresAt = typeof reward.expiresAt === 'function' ? reward.expiresAt() : reward.expiresAt;
          newActiveBoosts.push({
            type: 'KEYS_BOOST',
            name: 'Keys x2 (Until Midnight)',
            activatedAt: Date.now(),
            expiresAt: expiresAt || getMidnightExpiry(),
            keysMultiplier: reward.multiplier || 2
          });
        } else if (reward.type === 'powerup') {
          const powerUpConfig = ECONOMY_POWER_UPS[reward.item];
          if (powerUpConfig) {
            for (let i = 0; i < reward.quantity; i++) {
              newActiveBoosts.push({
                type: reward.item,
                name: powerUpConfig.name,
                activatedAt: Date.now(),
                expiresAt: Date.now() + powerUpConfig.duration,
                ...powerUpConfig
              });
            }
          }
        }
      });

      // Update userData
      const updates = {
        totalPoints: newPoints,
        manna: newManna,
        mannaLastUpdated: Date.now(),
        mannaEarningActive: userData.mannaEarningActive || mannaEarningActivated,
        keys: newKeys,
        activeBoosts: newActiveBoosts
      };

      setUserData(prev => ({
        ...prev,
        ...updates
      }));

      // Update localStorage
      const newData = {
        ...dailyChestsData,
        opened: [...(dailyChestsData.opened || []), chest.id]
      };
      setDailyChestsData(newData);
      localStorage.setItem('freeDailyChests', JSON.stringify(newData));

      // Sync to Firebase
      if (userId) {
        updateUserProgress(userId, updates).catch(err =>
          console.error('Error syncing chest rewards:', err)
        );
      }

      setOpening(null);
      setShowRewards(chest);

      // Auto-hide rewards after 3 seconds
      setTimeout(() => {
        setShowRewards(null);
      }, 3000);
    }, 1500);
  };

  if (!dailyChestsData.date) {
    return null; // Still loading
  }

  const getNextChestActivation = (date) => {
    const hour = date.getHours();
    const nextActivation = new Date(date);

    if (hour >= 3 && hour < 15) {
      nextActivation.setHours(15, 0, 0, 0);
      return { chestName: 'Evening Grace', activationTime: nextActivation };
    }

    nextActivation.setHours(3, 0, 0, 0);
    if (hour >= 15) {
      nextActivation.setDate(nextActivation.getDate() + 1);
    }

    return { chestName: 'Morning Blessing', activationTime: nextActivation };
  };

  const formatCountdown = (milliseconds) => {
    const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const nextChestActivation = getNextChestActivation(currentTime);
  const timeUntilNextActivation = nextChestActivation.activationTime.getTime() - currentTime.getTime();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-800 to-slate-900 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Next Chest Activation Countdown */}
        <div className="mb-6 text-center">
          <div className="inline-block bg-gradient-to-r from-blue-600/30 to-purple-600/30 border-2 border-blue-500/50 rounded-xl px-6 py-3">
            <div className="flex items-center justify-center gap-3">
              <Clock size={24} className="text-blue-400" />
              <div className="text-left">
                <div className="text-xs uppercase tracking-wide text-blue-200/90">
                  Next {nextChestActivation.chestName} in
                </div>
                <div className="text-2xl font-mono font-bold text-white">
                  {formatCountdown(timeUntilNextActivation)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-end mb-4">
            <button
              onClick={onCancel}
              className="text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft size={24} />
            </button>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-3">
              <span className="text-4xl">🎁</span>
              <div>
                <h1 className="text-3xl font-bold text-white">Daily Rewards</h1>
              </div>
            </div>
            <p className="text-slate-400 mt-2">Claim 2 free rewards every day!</p>
          </div>
        </div>

        {/* Reset Info & Time Restrictions */}
        <div className="bg-slate-800/50 rounded-xl p-4 border border-amber-500/30 mb-6 space-y-2">
          <div className="flex items-center justify-center gap-2 text-slate-300">
            <Clock size={18} className="text-amber-400" />
            <span className="text-sm">Rewards reset daily at midnight</span>
          </div>
          <div className="text-center text-xs text-slate-400 space-y-1">
            <div>🌅 <span className="text-amber-300">Morning Blessing</span>: Available until 3:00 PM (your timezone)</div>
            <div>🌙 <span className="text-blue-300">Evening Grace</span>: Available after 3:00 PM (your timezone)</div>
          </div>
        </div>

        {/* Chests Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {DAILY_CHESTS.map(chest => {
            const opened = isChestOpened(chest.id);
            const isOpening = opening === chest.id;
            const isShowingRewards = showRewards?.id === chest.id;
            const available = isChestAvailable(chest.id);

            return (
              <div key={chest.id} className="relative">
                <div
                  className={`relative bg-gradient-to-br ${chest.color} rounded-2xl p-8 transition-all duration-500 ${
                    opened || !available ? 'opacity-60' : 'opacity-100 hover:scale-105 cursor-pointer'
                  } ${!opened && !isOpening && available ? 'shadow-2xl hover:shadow-3xl' : ''} ${
                    !opened && available ? `shadow-2xl ${chest.glowColor} animate-pulse` : ''
                  }`}
                  onClick={() => !opened && !isOpening && openChest(chest)}
                >
                  {/* Glowing Effect */}
                  {!opened && !isOpening && (
                    <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-2xl pointer-events-none animate-pulse" />
                  )}

                  {/* Chest Icon/Image */}
                  <div className="text-center mb-4">
                    <div
                      className={`transition-transform duration-500 ${
                        isOpening ? 'animate-bounce scale-125' : opened ? 'scale-90 opacity-50' : 'scale-100'
                      }`}
                    >
                      {chest.isImage ? (
                        <div className="flex justify-center">
                          <img src={chest.icon} alt={chest.title} className="w-24 h-24 object-contain" />
                        </div>
                      ) : (
                        <div className="text-8xl">{chest.icon}</div>
                      )}
                    </div>
                  </div>

                  {/* Title */}
                  <div className="text-center mb-4">
                    <h3 className="text-2xl font-bold text-white mb-1">{chest.title}</h3>
                    <p className="text-white/80 text-sm">{chest.subtitle}</p>
                  </div>

                  {/* Rewards Preview (when not opened) */}
                  {!opened && !isShowingRewards && (
                    <div className="space-y-2 text-white/90 text-sm">
                      {chest.rewards.map((reward, idx) => (
                        <div key={idx} className="flex items-center gap-2 justify-center">
                          {reward.type === 'points' ? (
                            <>
                              <Star size={16} className="text-yellow-300" />
                              <span className="font-semibold">{reward.amount} points</span>
                            </>
                          ) : reward.type === 'manna' ? (
                            <>
                              <span className="text-xl">🌾</span>
                              <span className="font-semibold">{reward.amount} Manna</span>
                            </>
                          ) : reward.type === 'manna-activation' ? (
                            <>
                              <span className="text-xl">🌾</span>
                              <span className="font-semibold text-center">{reward.description}</span>
                            </>
                          ) : reward.type === 'keys' ? (
                            <>
                              <span className="text-xl">🔑</span>
                              <span className="font-semibold">{reward.amount} Keys</span>
                            </>
                          ) : reward.type === 'protection' ? (
                            <>
                              <Sparkles size={16} className="text-cyan-300" />
                              <span className="font-semibold text-center">{reward.description}</span>
                            </>
                          ) : reward.type === 'powerup' ? (
                            <>
                              <Sparkles size={16} className="text-cyan-300" />
                              <span className="font-semibold">
                                {ECONOMY_POWER_UPS[reward.item]?.name} x{reward.quantity}
                              </span>
                            </>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Status */}
                  <div className="mt-6 text-center">
                    {opened ? (
                      <div className="text-white/70 font-bold text-sm">
                        <div className="flex items-center justify-center gap-2 mb-1">
                          <Clock size={16} />
                          <span>Opened - Reset at Midnight</span>
                        </div>
                        <div className="text-xs text-white/50">
                          {getTimeRestrictionMessage(chest.id)}
                        </div>
                      </div>
                    ) : !available ? (
                      <div className="text-white/70 font-bold text-sm">
                        <div className="flex items-center justify-center gap-2 mb-1">
                          <Clock size={16} />
                          <span>Not Available</span>
                        </div>
                        <div className="text-xs text-white/50">
                          {getTimeRestrictionMessage(chest.id)}
                        </div>
                      </div>
                    ) : isOpening ? (
                      <div className="text-white font-bold text-lg animate-pulse">
                        Opening...
                      </div>
                    ) : (
                      <div className="text-white font-bold text-lg animate-pulse">
                        Tap to Open!
                      </div>
                    )}
                  </div>
                </div>

                {/* Rewards Popup */}
                {isShowingRewards && (
                  <div className="absolute inset-0 flex items-center justify-center z-10">
                    <div className="bg-slate-900/95 border-4 border-amber-400 rounded-2xl p-8 shadow-2xl shadow-amber-500/50 animate-bounce-in">
                      <div className="text-center">
                        <div className="text-6xl mb-4">🎉</div>
                        <h3 className="text-2xl font-bold text-amber-400 mb-4">
                          {chest.title} Opened!
                        </h3>
                        <div className="space-y-3">
                          {chest.rewards.map((reward, idx) => (
                            <div key={idx} className="text-white text-lg font-semibold">
                              {reward.type === 'points' && (
                                <div className="flex items-center gap-2 justify-center">
                                  <Star size={24} className="text-yellow-300" />
                                  <span>+{reward.amount} Points</span>
                                </div>
                              )}
                              {reward.type === 'manna' && (
                                <div className="flex items-center gap-2 justify-center">
                                  <span className="text-3xl">🌾</span>
                                  <span>+{reward.amount} Manna</span>
                                </div>
                              )}
                              {reward.type === 'manna-activation' && (
                                <div className="flex flex-col items-center gap-2 justify-center">
                                  <span className="text-3xl">🌾</span>
                                  <span className="text-center">{reward.description}</span>
                                  <span className="text-sm text-amber-300">Active for today!</span>
                                </div>
                              )}
                              {reward.type === 'powerup' && (
                                <div className="flex items-center gap-2 justify-center">
                                  <Sparkles size={24} className="text-cyan-300" />
                                  <span>{ECONOMY_POWER_UPS[reward.item]?.name}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Info Box */}
        <div className="mt-8 bg-gradient-to-br from-blue-900/30 to-indigo-900/30 rounded-xl p-6 border border-blue-500/30">
          <h3 className="text-xl font-bold text-blue-300 mb-3 flex items-center gap-2">
            <Sparkles size={24} />
            About Manna 🌾
          </h3>
          <p className="text-slate-300 mb-3">
            <strong className="text-blue-300">Manna</strong> is a special daily-only currency that cannot be saved past 24 hours.
            Just like the manna in the wilderness, it must be used each day!
          </p>
          <div className="space-y-2 text-slate-300 text-sm">
            <div className="flex items-start gap-2">
              <Star size={16} className="text-amber-400 mt-1 flex-shrink-0" />
              <span><strong>Earn Manna by:</strong> Opening Morning Blessing chest, completing daily verses</span>
            </div>
            <div className="flex items-start gap-2">
              <Star size={16} className="text-amber-400 mt-1 flex-shrink-0" />
              <span><strong>Use Manna for:</strong> Daily boosts, hints, retries, and quality-of-life features</span>
            </div>
            <div className="flex items-start gap-2">
              <Clock size={16} className="text-red-400 mt-1 flex-shrink-0" />
              <span><strong>Expires:</strong> Unused Manna resets to 0 at midnight each day</span>
            </div>
          </div>
        </div>

        {/* Daily Missions */}
        <div className="mt-8">
          <DailyMissionBoard
            userData={userData}
            setUserData={setUserData}
            onMissionComplete={(mission) => {
              showToast(`Mission Complete!\n\n${mission.title}\n+${mission.reward} points`, 'success');
            }}
            onNavigate={onNavigate}
            onStartQuiz={onStartQuiz}
          />
        </div>
      </div>
    </div>
  );
};

export default DailyChestsPage;

